import { Box, Flex, HStack, Text, VStack } from "@chakra-ui/react";
import { useRef, useState } from "react";
import { File, FilePdf, FileDoc, FileXls, FileCsv, FilePpt, FileText, GlobeSimple, UploadSimple, X } from "@phosphor-icons/react";
import FileDragDrop from "components/FileDragDrop";
import FileDownloadButton from "components/FileDownloadButton";
import { getFileType } from "api/common/fileinfo";
import { apiUpload } from "api/common/fileinfo.service";
import { useQueryClient } from "react-query";
import FileListWithBrowseOrUpload from "../../components/FileListWithBrowseOrUpload";
import RichTextEditor from "../../IssbDataInput/components/RichTextEditor/RichTextEditor";

const SOURCE_FILE_TYPE_ICONS = {
  PDF: FilePdf, DOCX: FileDoc, DOC: FileDoc, XLSX: FileXls, XLS: FileXls,
  CSV: FileCsv, PPTX: FilePpt, PPT: FilePpt, TXT: FileText, URL: GlobeSimple,
};

const MAX_FILE_SIZE_MB = Number(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_FILE_SIZE_READABLE = `${MAX_FILE_SIZE_MB}MB`;

const StandardLayout = ({
  questionData,
  canSave,
  setCurrentAnswer,
  fileInfoList,
  onFileInfoListChange,
  fileFolderForRound,
  tags,
  showDropZone,
}) => {
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    const newFiles = [...(fileInfoList || [])];
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE_BYTES) continue;
      const formData = new FormData();
      formData.append("file", file);
      if (tags) formData.append("tags", tags);
      if (fileFolderForRound) formData.append("folders", [fileFolderForRound]);
      try {
        const newFileInfo = await apiUpload({ data: formData });
        newFiles.push(newFileInfo);
      } catch (err) {
        console.error("Error uploading file", err);
      }
    }
    queryClient.invalidateQueries("fileinfoService.list");
    onFileInfoListChange(newFiles);
    setUploading(false);
  };

  return (
    <Flex direction="column" gap={4}>
      {showDropZone && (
        <>
          <Flex gap={4}>
            {/* Documents added panel */}
            {fileInfoList?.length > 0 && (
            <Box
              flex={1}
              bg="darkTeal"
              border="1px solid"
              borderColor="darkTeal"
              borderRadius="lg"
              p={4}
            >
              <Text fontSize="sm" fontWeight="600" color="white" mb={3}>
                Sources Added
              </Text>
              <Box borderTop="1px solid" borderColor="whiteAlpha.300">
                {fileInfoList.map((fileinfo) => {
                  const fileType = getFileType(fileinfo);
                  const IconComponent = SOURCE_FILE_TYPE_ICONS[fileType] || File;
                  return (
                    <HStack
                      key={fileinfo.id}
                      py={1.5}
                      px={1}
                      spacing={2}
                      borderBottom="1px solid"
                      borderColor="whiteAlpha.300"
                    >
                      <Box flexShrink={0}>
                        <IconComponent size={22} color="white" />
                      </Box>
                      <FileDownloadButton fileInfo={fileinfo} as={Text} fontSize="body.xs" color="white" flex={1} noOfLines={1}>
                        {fileinfo.title || fileinfo.filename || fileinfo.name || fileinfo.id}
                      </FileDownloadButton>
                      {canSave && (
                        <Box
                          as="button"
                          flexShrink={0}
                          onClick={() => onFileInfoListChange(fileInfoList.filter((f) => f.id !== fileinfo.id))}
                          title="Remove"
                          _hover={{ color: "red.300" }}
                          color="white"
                        >
                          <X size={16} />
                        </Box>
                      )}
                    </HStack>
                  );
                })}
              </Box>
            </Box>
            )}

            {/* Drop zone */}
            {canSave && (
              <Box flex={1}>
                <FileDragDrop onFiles={handleFileUpload} borderRadius="lg">
                  <VStack
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius="lg"
                    py={10}
                    px={6}
                    spacing={3}
                    cursor="pointer"
                    h="100%"
                    justifyContent="center"
                    _hover={{ borderColor: "gray.400" }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <UploadSimple size={32} color="#85C22A" />
                    <Text fontWeight="semibold" textAlign="center">
                      {uploading ? "Uploading..." : "Drop files here or click to browse"}
                    </Text>
                    <Text fontSize="sm" color="gray.500">PDF, DOCX, XLSX, CSV up to {MAX_FILE_SIZE_READABLE}</Text>
                  </VStack>
                </FileDragDrop>
              </Box>
            )}
          </Flex>
          {canSave && (
            <input
              type="file"
              multiple
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => { handleFileUpload(e.target.files); e.target.value = null; }}
              accept=".pdf,.docx,.xlsx,.xls,.doc,.csv"
            />
          )}
        </>
      )}

      {!showDropZone && (
        <FileListWithBrowseOrUpload
          fileLabel="data sources"
          readOnly={!canSave}
          onFileInfoListChange={onFileInfoListChange}
          fileInfoList={fileInfoList}
          tags={tags}
          folder={fileFolderForRound}
        />
      )}

      <Text fontSize="sm" fontWeight="600" color="gray.600">Additional Information</Text>
      <RichTextEditor
        content={questionData.answer || ""}
        onChange={(html) => setCurrentAnswer(html)}
        isDisabled={!canSave}
      />
    </Flex>
  );
};

export default StandardLayout;
